import { useEffect, useRef } from 'react'

const LONG_PRESS_MS = 500

/**
 * Touch + mouse long-press hook. Uses pointer events so it fires for
 * both real touches and mouse hold (DevTools phone emulation).
 *
 * Returns spreadable handlers plus a `didLongPress()` getter — call it
 * inside the same element's onClick to swallow the tap-through that
 * follows a long-press release.
 */
export function useLongPress(onLongPress, delay = LONG_PRESS_MS) {
  const timer = useRef(null)
  const triggered = useRef(false)
  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }
  // Unmount cleanup — if a row unmounts mid-press (operator scrolls
  // away, or the list refreshes during a hold), kill the pending
  // timer so the onLongPress callback doesn't fire against a dead
  // tree.
  useEffect(() => () => clear(), [])
  return {
    onPointerDown: () => {
      triggered.current = false
      timer.current = setTimeout(() => {
        triggered.current = true
        onLongPress()
      }, delay)
    },
    onPointerUp: clear,
    onPointerMove: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
    didLongPress: () => triggered.current
  }
}

/**
 * Relative-time formatter — "just now" / "5 minutes ago" / "3 days ago".
 * Pure; safe to import anywhere. Returns '' for invalid input.
 */
export function formatRelativeTime(isoString, now = new Date()) {
  if (!isoString) return ''
  const then = new Date(isoString)
  if (isNaN(then.getTime())) return ''
  const diffMs = now - then
  if (diffMs < 0) return 'just now'
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`
  const years = Math.floor(months / 12)
  return `${years} year${years === 1 ? '' : 's'} ago`
}
